/**
 * @file app-modal.ts
 *
 * Modal management handlers for as-app: transient mesh/transfer panels,
 * error panels, named overlay panels, and unsaved-warning / clickthru helpers.
 * All exported functions take a ModalContext as their first argument.
 *
 * Note: listener registration for 'as-modal-closed' is intentionally left to
 * the caller (as-app.ts) so the same arrow-function reference can be used for
 * both addEventListener and removeEventListener.
 */

import type { AsModalOverlay } from '../overlays/as-modal-overlay.ts';
import { buildModalHTML, type ModalContent } from './modal-templates.ts';
import { appInfo } from './app-config.ts';
import { devlog } from '../../logic/utils.ts';

// ============================================================================
// Context interface — satisfied by AsApp directly
// ============================================================================

export interface ModalContext {
  readonly appId: string;
  modalContent: ModalContent;
  isHomeRedirectPending: boolean;
  readonly modalOverlay: AsModalOverlay | null;
  readonly otherAppsForTransfer: string[];
  openTransferInNewTab: boolean;
}

// ============================================================================
// Transient modal (mesh transfer etc.)
// ============================================================================

/** Renders and opens the transient modal panel. Does not register listeners. */
export const showModal = (ctx: ModalContext): void => {
  if (ctx.modalContent.type === 'none') return;
  devlog('Showing modal with type:', ctx.modalContent.type);

  const overlay = ctx.modalOverlay;
  if (!overlay) { console.error('Could not find as-app-overlay in shadow DOM'); return; }

  const panelId = `modal-panel-${ctx.modalContent.type}`;
  const html = buildModalHTML(ctx.modalContent, ctx.otherAppsForTransfer, appInfo, ctx.openTransferInNewTab, ctx.appId);

  const errorPanel = overlay.querySelector('#main-error-panel');
  overlay.innerHTML = html;
  if (errorPanel) {
    overlay.appendChild(errorPanel);
  } else {
    const newErrorPanel = document.createElement('as-error-info');
    newErrorPanel.id = 'main-error-panel';
    overlay.appendChild(newErrorPanel);
  }
  overlay.querySelector('#main-error-panel')?.classList.remove('visible');

  overlay.setAttribute('visible-child', panelId);
  overlay.open();
};

/** Cleans up after a transient modal closes. */
export const onModalClosed = (ctx: ModalContext): void => {
  ctx.modalContent = { type: 'none' };
  ctx.isHomeRedirectPending = false;
  if (!ctx.modalOverlay) return;
  ctx.modalOverlay.querySelectorAll('[id^="modal-panel-"]').forEach(el => el.remove());
  const errorPanel = ctx.modalOverlay.querySelector('#main-error-panel');
  if (errorPanel) {
    errorPanel.classList.add('visible');
    ctx.modalOverlay.setAttribute('visible-child', 'main-error-panel');
  }
};

// ============================================================================
// Named overlay panels
// ============================================================================

export const openModal = (ctx: ModalContext, modalId: string, tagName: string, attributes?: Record<string, string>): void => {
  const overlay = ctx.modalOverlay;
  if (!overlay) return;
  let el = overlay.querySelector(`#${modalId}`) as HTMLElement | null;
  if (!el) {
    el = document.createElement(tagName);
    el.id = modalId;
    overlay.appendChild(el);
  }
  if (attributes) Object.entries(attributes).forEach(([k, v]) => el!.setAttribute(k, v));
  overlay.setAttribute('visible-child', modalId);
  overlay.open(modalId);
};

export const showErrorModal = (ctx: ModalContext, detail: { error: Error | string; origin?: string; info?: string }): void => {
  const overlay = ctx.modalOverlay;
  if (!overlay) return;
  const panel = overlay.querySelector('#main-error-panel');
  // as-error-info only fetches system info (incl. sessionId) once at mount —
  // refresh it here so the id shown reflects the session at error time, not
  // whatever was live when the page first loaded. handleSystemInfoReq is a
  // window listener keyed off detail.requester, so this must dispatch on window.
  if (panel) window.dispatchEvent(new CustomEvent('get-system-info', { detail: { requester: panel } }));
  panel?.dispatchEvent(new CustomEvent('new-error', { detail }));
  overlay.setAttribute('visible-child', 'main-error-panel');
  overlay.open('main-error-panel');
};
